import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle2, FileText, Loader2, PenLine, RotateCcw } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PublicFormSignPage — link público (token único, no adivinable) para que el
 * cliente firme UNA forma (Permit Application u otra) sin necesidad de
 * cuenta. Mismo patrón que /estimate/:token, pero para el "Office Workflow".
 * Solo pide LA firma del cliente (Owner/Agent) — la firma del contratista
 * ya viene pre-estampada por el sistema (Settings > Company).
 */
export default function PublicFormSignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signingError, setSigningError] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const sigCanvasRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/public/form-sign/${token}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => { setData(d); if (d.already_signed) setSigned(true); })
      .catch(() => setError('We could not find this document. The link may have expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    setSigningError('');
    if (sigCanvasRef.current && sigCanvasRef.current.isEmpty()) {
      setSigningError('Please draw your signature before continuing.');
      return;
    }
    if (!signerName.trim()) {
      setSigningError('Please enter your full name.');
      return;
    }
    setIsSigning(true);
    try {
      // NOTE: getTrimmedCanvas() is broken in react-signature-canvas@1.1.0-alpha.2
      // when bundled via CRA/webpack5 (resolves the package's ESM build, whose
      // `trim-canvas` CJS interop is broken -> "trim_canvas...is not a function").
      // getCanvas() + toDataURL() avoids the buggy trim step entirely (loses only
      // the whitespace auto-crop, signature itself is captured correctly).
      const signatureImage = sigCanvasRef.current.getCanvas().toDataURL('image/png');
      const res = await fetch(`${API_URL}/api/public/form-sign/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signer_name: signerName.trim(), signature_image: signatureImage }),
      });
      if (res.ok) {
        setSigned(true);
        setData(prev => ({ ...prev, signer_name: signerName.trim() }));
      } else {
        const err = await res.json().catch(() => ({}));
        setSigningError(err.detail || 'Could not submit the signature.');
      }
    } catch {
      setSigningError('Could not submit the signature. Please try again.');
    } finally {
      setIsSigning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Card className="max-w-md"><CardContent className="p-8 text-center">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500 font-medium" data-testid="form-sign-error">{error}</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div data-testid="public-form-sign-page" className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-[#001a33] to-[#003366] text-white">
        <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-bold font-['Barlow']">BW</span>
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold font-['Barlow']">Blue Well Drilling</h1>
            <p className="text-blue-200 text-xs truncate">{data.name}{data.property_address ? ` · ${data.property_address}` : ''}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* PDF preview */}
        <Card className="bg-white border-[#003366]/20 overflow-hidden">
          <CardContent className="p-0">
            <iframe
              src={`${API_URL}${data.pdf_url}`}
              title={data.name}
              className="w-full border-0"
              style={{ height: '70vh' }}
              data-testid="form-sign-pdf-preview"
            />
          </CardContent>
        </Card>

        {!signed ? (
          <Card className="bg-white border-[#003366]/20">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <PenLine className="w-5 h-5 text-[#003366]" />
                <h3 className="text-sm font-semibold text-gray-800">Sign Document</h3>
              </div>
              <p className="text-xs text-gray-500">
                By signing, you confirm you have reviewed the document above and accept its content.
              </p>

              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-600">Digital Signature <span className="text-red-500">*</span></p>
                <div className="relative border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50 hover:border-[#003366]/30 transition-colors">
                  <SignatureCanvas
                    ref={sigCanvasRef}
                    penColor="#003366"
                    canvasProps={{ className: 'w-full', style: { height: '140px', touchAction: 'none' } }}
                    backgroundColor="rgb(249,250,251)"
                  />
                  <button
                    type="button"
                    onClick={() => sigCanvasRef.current?.clear()}
                    className="absolute top-2 right-2 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 bg-white/80 px-2 py-1 rounded-lg border border-gray-200 transition-colors"
                    data-testid="form-sign-clear-btn"
                  >
                    <RotateCcw className="w-3 h-3" /> Clear
                  </button>
                  <p className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-gray-300 pointer-events-none select-none">
                    Sign here with your finger or mouse
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600">Full Name <span className="text-red-500">*</span></label>
                <Input
                  data-testid="form-sign-name-input"
                  value={signerName}
                  onChange={e => setSignerName(e.target.value)}
                  placeholder="Enter your full name"
                  className="text-sm"
                />
              </div>

              {signingError && (
                <p className="text-xs text-red-500 font-medium" data-testid="form-sign-error-msg">{signingError}</p>
              )}

              <Button
                data-testid="form-sign-submit-btn"
                onClick={handleSubmit}
                disabled={isSigning}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 text-base rounded-xl shadow-lg disabled:opacity-50"
              >
                {isSigning ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><CheckCircle2 className="w-5 h-5 mr-2" /> Sign Document</>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center gap-3" data-testid="form-sign-success">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">Document signed successfully</p>
              {(data.signer_name || signerName) && (
                <p className="text-xs text-emerald-600">Signed by: <strong>{data.signer_name || signerName}</strong></p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
